/**
 * Centralized Date Utilities
 * Replaces legacy files: date.js, date_v2.js, dateSimple.js, etc.
 */

const MONTH_NAMES_ES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
]

const MONTH_NAMES_SHORT_EN = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dic' // Keeping 'Dic' as per original code legacy, though standard EN is Dec
]

const DAY_NAMES_ES = [
    'Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'
]

const isValidDate = (date) => {
    return date && !isNaN(new Date(date).getTime())
}

const pad = (num) => num.toString().padStart(2, '0')

/**
 * Returns day of the month (1-31)
 * Legacy: fechaDia
 */
export const getDay = (time, isLocal = true) => {
    if (!isValidDate(time)) return 'Sin fecha'
    const date = new Date(time)
    if (!isLocal) return date.getUTCDate()

    return new Intl.DateTimeFormat('es-CL', {
        timeZone: 'America/Santiago', day: 'numeric'
    }).formatToParts(date).find(p => p.type === 'day').value
}

/**
 * Returns month number (1-12)
 * Legacy: fechaMes
 */
export const getMonth = (time, isLocal = true) => {
    if (!isValidDate(time)) return 'Sin fecha'
    const date = new Date(time)
    if (!isLocal) return date.getUTCMonth() + 1

    return new Intl.DateTimeFormat('es-CL', {
        timeZone: 'America/Santiago', month: 'numeric'
    }).formatToParts(date).find(p => p.type === 'month').value
}

/**
 * Returns full year
 * Legacy: fechaAno
 */
export const getYear = (time, isLocal = true) => {
    if (!isValidDate(time)) return 'Sin fecha'
    const date = new Date(time)
    if (!isLocal) return date.getUTCFullYear()

    return new Intl.DateTimeFormat('es-CL', {
        timeZone: 'America/Santiago', year: 'numeric'
    }).formatToParts(date).find(p => p.type === 'year').value
}

/**
 * Format: "DD/MM/YYYY"
 * Legacy: dateSimple.js
 */
export const formatDateSimple = (time, isLocal = true) => {
    if (!isValidDate(time)) return 'No informado'
    const date = new Date(time)
    const parts = new Intl.DateTimeFormat('es-CL', {
        timeZone: isLocal ? 'America/Santiago' : 'UTC',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    }).formatToParts(date).reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {})
    return `${parts.day}/${parts.month}/${parts.year}`
}

/**
 * Format: "DOM 12 DE ENERO" (Upper Case)
 * Legacy: date.js
 */
export const formatDateLong = (time, isLocal = true) => {
    if (!isValidDate(time)) return 'SIN INFORMACIÓN'
    const date = new Date(time)
    const parts = new Intl.DateTimeFormat('es-CL', {
        timeZone: isLocal ? 'America/Santiago' : 'UTC',
        weekday: 'short',
        day: '2-digit',
        month: 'long'
    }).formatToParts(date).reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {})

    const dayName = parts.weekday.substring(0, 3)
    const day = parts.day
    const monthName = parts.month
    return `${dayName} ${day} DE ${monthName}`.toUpperCase()
}

/**
 * Format: "DD-Mon-YYYY" (e.g., 01-Jan-2023)
 * Legacy: dateWithYear.js, fechaCompleta
 */
export const formatDateWithYear = (time, isLocal = true) => {
    if (!isValidDate(time)) return 'Sin fecha'
    const date = new Date(time)
    const parts = new Intl.DateTimeFormat('en-US', { // en-US for short month names
        timeZone: isLocal ? 'America/Santiago' : 'UTC',
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    }).formatToParts(date).reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {})

    const d = parts.day
    const m = parts.month === 'Dec' ? 'Dic' : parts.month // Legacy compatibility for 'Dic'
    const y = parts.year
    return `${d}-${m}-${y}`
}

/**
 * Format: "DD/MM/YYYY HH:MM:SS" (Locale ES)
 * Legacy: dateWithTime.js
 */
export const formatDateWithTime = (time, isLocal = true) => {
    if (!isValidDate(time)) return 'Sin información'
    const date = new Date(time)
    return date.toLocaleString('es-CL', {
        timeZone: isLocal ? 'America/Santiago' : 'UTC',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    }).replace(',', '')
}

/**
 * Format: "HH:MM"
 * Legacy: hour.js, time.js
 */
export const formatTime = (time, isLocal = true) => {
    if (!isValidDate(time)) return 'Sin información'
    const date = new Date(time)
    const parts = new Intl.DateTimeFormat('es-CL', {
        timeZone: isLocal ? 'America/Santiago' : 'UTC',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    }).formatToParts(date).reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {})
    return `${parts.hour}:${parts.minute}`
}

/**
 * Complex formatter handling multiple types.
 * Legacy: date_v2.js
 */
export const dateV2 = (value, type, isLocal = true) => {
    if (!isValidDate(value)) return 'Sin información'

    const date = new Date(value)

    // Force Chile timezone (America/Santiago) to handle UTC vs CLT/CLST differences
    // or use UTC if isLocal is false (useful for midnight dates like datePrev)
    const parts = new Intl.DateTimeFormat('es-CL', {
        timeZone: isLocal ? 'America/Santiago' : 'UTC',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    }).formatToParts(date).reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {})

    const year = parts.year
    const month = parts.month
    const day = parts.day
    const hour = parts.hour
    const min = parts.minute
    const sec = parts.second

    if (type === 'full') return `${day}-${month}-${year} ${hour}:${min}:${sec}`
    if (type === 'yyyy/mm/dd') return `${year}/${month}/${day}`
    if (type === 'dd/mm/yyyy') return `${day}/${month}/${year}`

    if (type === 'dd/mm/yyyy hh:mm') return `${day}/${month}/${year} ${hour}:${min}`
    if (type === 'yyyymmddhhmm') return `${year}${month}${day}${hour}${min}`

    return ''
}

// Determine if a date string is valid
export const isDateValid = isValidDate
